// Handler commands — the standing orders a person actually gives a working dog.
//
// See PLAN.md § Playtest feedback P3. The player no longer drags the dog around; they give it the
// commands a shepherd gives, and the dog works the flock itself. What is being tested becomes
// reading the flock and timing the countermand, rather than mouse precision.
//
// A command is a STANDING ORDER: it persists until another command replaces it, exactly as it does
// in the field. A dog told to come by keeps flanking until it is told to lie down. Two of the six
// are goal-directed rather than continuous, and that is also true of the real commands:
//
//   come by    — flank clockwise around the flock, holding a wide working distance
//   away to me — flank anti-clockwise (the mirror of come by)
//   walk on    — close in and drive: sustained pressure straight through the flock
//   get behind — take the balance point, opposite the gate across the flock, and hold it there
//   steady     — ease off: half pace, and winds back accumulated walk-on pressure. Persists until
//                repeated. A modifier, not a direction.
//   get back   — work from further out; repeat it to give the mob more and more room
//   look back  — turn off this mob and go back for the stock left behind
//   drop       — stop, and take the pressure off without moving away
//   that'll do — come off the sheep entirely; the release
//
// DETERMINISM. This runs in the sim path, so `Math.sin/cos/atan2` are banned outright — they are
// not correctly rounded and genuinely differ between V8, JSC and SpiderMonkey, which would break
// replay verification across browsers. Flanking therefore never computes an angle. A 90° rotation
// is an exact component swap, `(x,y) → (−y,x)`, so the dog orbits using nothing but `+ − × ÷` and
// one `sqrt`. There is a test asserting this file contains no transcendental.
//
// This module allocates nothing and holds no state — the order itself lives on `Simulation`.

import type { Vec2 } from './vec2'

export type CommandType =
  | 'come-by'
  | 'away'
  | 'walk-on'
  | 'steady'
  | 'get-back'
  | 'get-behind'
  | 'look-back'
  | 'drop'
  | 'thatll-do'

/**
 * Declaration order is the order the command bar shows them in, and the digit that fires each one.
 *
 * **Grouped by what the command is FOR, in two rows of the bar.** The first four say where the dog
 * goes; the last five say how hard it works, ordered from most pressure to none. Playtest found the
 * bar "small and hard to read", and half of that was the arrangement rather than the type size —
 * declaration order used to interleave the two kinds, so a player scanning for the release found it
 * between a flank and a modifier.
 *
 * The flanks sit either side of "get behind" because that is where they are in space: come by goes
 * round one way, away the other, and the balance point is between them.
 */
export const COMMANDS: readonly CommandType[] = [
  // Where the dog goes.
  'come-by',
  'get-behind',
  'away',
  'look-back',
  // How much pressure, most to least.
  'walk-on',
  'steady',
  'get-back',
  'drop',
  'thatll-do',
]

/** What a handler would say, for the command bar and the help screen. */
export const COMMAND_LABELS: Readonly<Record<CommandType, string>> = {
  'come-by': 'COME BY',
  away: 'AWAY',
  'walk-on': 'WALK ON',
  steady: 'STEADY',
  'get-back': 'GET BACK',
  'get-behind': 'GET BEHIND',
  'look-back': 'LOOK BACK',
  // "Drop" rather than "lie down": this is an Australian kelpie on an Australian property, and drop
  // is what the handler actually says.
  drop: 'DROP',
  'thatll-do': "THAT'LL DO",
}

/**
 * One line on what each command actually does, for the help screen.
 *
 * Kept here rather than in the UI on purpose: this describes engine behaviour, so if the behaviour
 * changes the wrong description is sitting in the same file as the change.
 */
export const COMMAND_HELP: Readonly<Record<CommandType, string>> = {
  'come-by': 'Cast clockwise around the flock. Stops at the head of the mob — press again to send the dog round once more.',
  away: 'The same, anticlockwise.',
  'walk-on':
    'Close in and drive, aiming them at the gate and working them along a fence rather than into it. Say it again to lift the pace and lean in.',
  steady:
    'Ease off. Half pace until you say it again, and it winds back the pressure that walking on has built up.',
  'get-back': 'Give them more room. Each time you say it the dog works from further out.',
  'get-behind': 'Take the balance point, opposite the gate, and hold it as the flock moves.',
  'look-back': 'Turn off the mob and go back for the ones left behind. Does nothing if the flock is all together.',
  drop: 'Stop, and take the pressure off without moving away. This is how a frightened flock settles.',
  'thatll-do': 'Come off the sheep entirely.',
}

const COMMAND_SET = new Set<string>(COMMANDS)

/**
 * Narrow an input event type to a command.
 *
 * A `Set` is safe here precisely because it is never *iterated* — membership testing has no order
 * to depend on. The determinism rule bans `Set`/`Map` iteration in the hot path, not lookup.
 */
export function isCommand(type: string): type is CommandType {
  return COMMAND_SET.has(type)
}

/**
 * How the dog holds station under each order.
 *
 * **Every factor is a multiple of `dogRadius` measured from the EDGE of the mob, not from its
 * centroid**, and that distinction is a bug fix rather than a refinement. Measured from the centroid,
 * a 22-sheep mob whose own radius is 7 m put the dog *inside* the flock at a nominal 7.65 m station;
 * and the nearest sheep — which is what actually feels the pressure — sat at whatever distance the
 * mob's spread happened to leave. Working off the back of the mob rather than a distance from the
 * centroid is the same lesson as PLAN.md § Open problem 2a.
 */
export interface CommandConfig {
  /**
   * Working distance while flanking. Inside the falloff, so a flank bends the mob as it goes round
   * rather than repositioning an inert dog.
   */
  flankRadiusFactor: number
  /**
   * Working distance while driving. 0.5 is measured rather than chosen — see § Open problem 2a,
   * where standing off the flock's edge was shown to apply no pressure at all.
   */
  walkOnRadiusFactor: number
  /**
   * Where the dog stands when it is holding rather than working — "get behind", and the stop at the
   * end of a flank.
   *
   * **1.0 puts the nearest sheep exactly on the falloff edge, where the dog's force is precisely
   * zero.** That surface used to be a bug (PLAN.md § Open problem 2a: a stable equilibrium the flock
   * parked on and the dog could never push from) and it is exactly the right property for a command
   * that means "be present, do not push".
   *
   * This is what separates holding from driving. With `get behind` holding at the flanking distance
   * instead, the dog pushed constantly from the side opposite the gate — which is the definition of
   * driving the mob home — and the command solved 34 of 42 sampled levels **on its own**, with the
   * player doing nothing. A command that plays the game is not a command.
   */
  holdRadiusFactor: number
  /**
   * How much closer each repeat of "walk on" brings the dog, as a fraction of `dogRadius`.
   *
   * A handler leans on a mob harder by pushing the dog in, not by shouting. Re-issuing the command
   * is the same gesture as re-issuing a flank to override the lift — the vocabulary is small, so
   * repetition has to carry the intensity.
   */
  walkOnPressStep: number
  /** Most repeats that count. Beyond this the dog is on top of the sheep and it stops meaning more. */
  walkOnPressMax: number
  /**
   * How much faster the dog works per notch of walk-on pace, as a fraction of `dogSpeed`.
   *
   * Pace is what "walk on" escalates: the dog comes in AND gets on with it. Applied in `stepDog`
   * rather than here, because it changes how fast the dog travels rather than where it is going.
   */
  walkOnPaceStep: number
  /**
   * The most a drive swings its push line toward the gate, as a cosine/sine pair.
   *
   * Getting behind the mob stays the player's job. An UNBOUNDED swing — even a small per-tick
   * fraction of the error — converges on balance and makes "walk on" perform the outrun by itself:
   * measured on P8's bench, walk-on alone went 1/42 to 25/42. Bounded against the line the dog stood
   * on when the order was given, it cannot.
   */
  walkOnLeadCos: number
  walkOnLeadSin: number
  /** How near a fence must be to the mob before a drive runs along it, in metres. */
  walkOnFenceRadius: number
  /**
   * How squarely a push must already be heading at the gate for the fence rule to stand aside.
   *
   * The gate is a gap in the perimeter, so on the final approach "do not drive them into the fence"
   * and "put them through that hole" are the same direction, and the fence rule must lose.
   */
  walkOnFenceSkipCos: number
  /** How much further out each "get back" puts the dog, as a fraction of `dogRadius`. */
  getBackStep: number
  /** Most "get back"s that count, so the dog cannot be sent into the next paddock. */
  getBackMax: number
  /**
   * Walk-on presses a single "steady" winds back.
   *
   * Walk-on accumulates, so there has to be a way down as well as up — and it has to be a way down
   * that does not stop the drive, or the only escape from maximum pressure is a different command.
   * Steady moderates; a new command resets.
   */
  steadyPressRelief: number
  /** Dog speed while "steady" is on, as a fraction of `dogSpeed`. */
  steadyFactor: number
  /**
   * How much tangent a flank keeps while it is still inside its working radius.
   *
   * Low, so getting clear of the mob beats getting round it. At 1 the dog spirals across the flock
   * at 45°; the measurement that forced this was a cast passing 4.0 m from the centre of a mob whose
   * own radius is nearly 9 m.
   */
  flankEscapeTangent: number
  /** Where the dog withdraws to on "that'll do". Well beyond the falloff, so pressure is fully off. */
  recallRadiusFactor: number
  /**
   * How hard a flank corrects its working distance, per metre of error, against the tangential run.
   * Higher spirals in and out; lower lets the dog drift off station while circling.
   */
  flankRadialGain: number
  /** Dog presence while dropped: repulsion and stress gain both scale by this. */
  dropPressure: number
}

export interface CommandWorld {
  dog: Vec2
  /** Centroid of the sheep still to be worked. */
  flock: Vec2
  /** Gate centre — the destination, and therefore what "behind" is measured against. */
  gate: Vec2
  /**
   * Write the position of the rearmost worked sheep along a unit direction into `out`.
   *
   * **Stations are offsets from that SHEEP, never distances from the centroid**, and this is the
   * lesson of PLAN.md § Open problem 2a re-learned the hard way. A centroid-plus-extent station
   * fails on any mob that is not a disc: the sheep furthest along the dog's axis can still be metres
   * off it sideways, so "centroid + reach + 4.5 m" leaves the dog outside its own influence radius
   * and touching nothing. Measured that way, "walk on" moved a mob **one metre in sixty seconds at
   * stress 0.00**. Standing 4.5 m behind the rear sheep's actual position puts the dog exactly
   * 4.5 m from that sheep, whatever shape the mob is.
   */
  rearAlong: (dx: number, dy: number, out: Vec2) => void
  /** Radial extent of the worked mob, for the flank — which orbits the mob rather than pushing it. */
  mobRadius: number
  dogRadius: number
  /**
   * How far ahead of itself to aim, in metres. `stepDog` lerps toward the target at `dogSpeed`, so
   * a target a few ticks out gives a stable heading instead of a jittering one.
   */
  lookahead: number
  /** Repeats of "walk on" since it was given, each one leaning the dog further in. */
  pressSteps: number
  /** Repeats of "get back", each one giving the mob more room. Applies to every station. */
  backSteps: number
  /**
   * The fence nearest the worked mob, if one is close enough to drive along.
   *
   * Supplied by the simulation as plain numbers rather than a segment, because this module is pure
   * geometry and allocating a descriptor every tick is exactly what the engine rules forbid.
   * `fenceAx/fenceAy` is a unit vector ALONG the fence, already oriented so that following it takes
   * the mob toward the gate; `fenceNx/fenceNy` is the unit normal pointing from the fence toward the
   * mob, which is what says whether a push would drive them into it. All four are zero when there is
   * no fence within `walkOnFenceRadius`.
   */
  fenceAx: number
  fenceAy: number
  fenceNx: number
  fenceNy: number
  /**
   * The radial the dog stood on when the current drive was ordered, as a unit vector.
   *
   * The drive's aim is measured from HERE rather than from where the dog is now, and that is what
   * bounds it. Re-anchoring every tick is the same thing as having no bound at all: each tick's
   * small correction becomes the next tick's starting point, the station creeps round to balance,
   * and the command starts playing the game. Repeats of "walk on" deliberately do NOT re-anchor
   * either, or tapping the button would ratchet the dog around the mob a notch at a time.
   */
  driveAnchorX: number
  driveAnchorY: number
}

/** Below this the dog is effectively on top of the centroid and the radial is meaningless. */
const DEGENERATE = 1e-6


/**
 * How far off balance the dog must get before a flank is allowed to end at the balance point.
 *
 * Without this, a flank given while the dog is already at balance would latch immediately and the
 * dog would never move — and "give the command again to send it round" is precisely how a handler
 * overrides the instinct to stop. 0.7 is about 45°.
 */
export const LIFT_ARM_COS = 0.7

/** Close enough to balance to call it arrived, for the case where the dog converges without crossing. */
export const LIFT_ENTER_COS = 0.98

/**
 * Where the dog is relative to the balance point, as seen from the mob.
 *
 * `aligned` is the cosine between (flock→dog) and the balance direction: 1 at the balance point,
 * −1 directly opposite it. `cross` is the signed side, so a change of sign means the dog has swept
 * *through* the balance ray. Detecting the crossing rather than a proximity window matters because
 * a dog flanking at a small radius covers a lot of angle per tick and would step straight over any
 * window narrow enough to be meaningful.
 *
 * Both are pure `+ − × ÷ sqrt` — no angle is ever formed.
 */
export function balanceAlignment(w: CommandWorld): { aligned: number; cross: number } {
  const rx = w.dog.x - w.flock.x
  const ry = w.dog.y - w.flock.y
  const rd = Math.sqrt(rx * rx + ry * ry)

  let bx = w.flock.x - w.gate.x
  let by = w.flock.y - w.gate.y
  const bd = Math.sqrt(bx * bx + by * by)

  if (rd < DEGENERATE || bd < DEGENERATE) return { aligned: 0, cross: 0 }

  const ux = rx / rd
  const uy = ry / rd
  bx /= bd
  by /= bd

  return { aligned: ux * bx + uy * by, cross: ux * by - uy * bx }
}

/**
 * Where the dog should be heading this tick under a standing order. Writes into `out`.
 *
 * Every order is recomputed from the CURRENT flock position each tick rather than resolved once
 * into a fixed point. That is what makes a standing order a standing order: told to hold the
 * balance point, the dog keeps holding it as the mob moves, which is what the real command means.
 */
export function commandTarget(
  order: CommandType,
  w: CommandWorld,
  cfg: CommandConfig,
  out: Vec2,
): void {
  // `steady` and `get-back` change HOW the dog works, not what it is doing, so they never reach here
  // as an order — the simulation applies them to whatever order is standing. A modifier that
  // replaced the standing order would mean asking a dog to slow down stopped it flanking.
  if (order === 'drop') {
    // Stop where you are. Pressure comes off through `dropPressure`, not by backing away —
    // that distinction is the whole value of the command.
    out.x = w.dog.x
    out.y = w.dog.y
    return
  }

  // The radial: from the flock out to the dog. Everything else is expressed against it.
  let rx = w.dog.x - w.flock.x
  let ry = w.dog.y - w.flock.y
  let d = Math.sqrt(rx * rx + ry * ry)

  if (d < DEGENERATE) {
    // The dog is standing on the mob, so "out" is undefined. Fall back to the line the gate
    // defines, which is deterministic and points somewhere useful — behind the flock.
    rx = w.flock.x - w.gate.x
    ry = w.flock.y - w.gate.y
    d = Math.sqrt(rx * rx + ry * ry)
    if (d < DEGENERATE) {
      // The flock is sitting in the gate. Nothing to work; hold still.
      out.x = w.dog.x
      out.y = w.dog.y
      return
    }
  }

  const ux = rx / d
  const uy = ry / d

  /**
   * How far beyond the stock to stand, in metres — the ordered distance plus whatever room "get
   * back" has been asked for.
   *
   * **Every station goes through here, and for a long time only one did.** `get back` was written to
   * widen whatever the dog was doing ("give them room" applies to a drive as much as to a cast), and
   * PLAN.md § P17 says so — but the offset was only ever applied to the flank, and a flank stops
   * using it the moment it lifts and holds. Measured across the five orders that position the dog,
   * four get-backs moved it by 0.19, 0.12, 0.00, 0.00 and 0.00 metres. The command did nothing at
   * all, which is exactly what playtest reported.
   */
  const offset = (factor: number): number =>
    w.dogRadius * (factor + w.backSteps * cfg.getBackStep)

  /**
   * The same, measured from the mob's centroid rather than from its rear sheep — so it has to clear
   * the mob's own extent first. Used by the flank, which orbits the centroid.
   */
  const station = (factor: number): number => w.mobRadius + offset(factor)

  // "look back" resolves to holding the balance of whichever mob it switched to — the switch itself
  // happens in the simulation, which is the only thing that knows where the other mobs are.
  if (order === 'get-behind' || order === 'look-back') {
    // The balance point: directly opposite the gate across the flock, so that driving from here
    // sends the mob at the gate. Computed from the gate rather than from where the dog happens to
    // be, which is what makes this a repositioning command rather than a flank.
    let bx = w.flock.x - w.gate.x
    let by = w.flock.y - w.gate.y
    const bd = Math.sqrt(bx * bx + by * by)
    if (bd < DEGENERATE) {
      // Flock already at the gate — hold the current line rather than picking an arbitrary one.
      bx = ux
      by = uy
    } else {
      bx /= bd
      by /= bd
    }
    // Holding distance, not working distance: the point of "get behind" is to be in the right place,
    // and a dog that pressures from the balance point is simply driving.
    w.rearAlong(bx, by, out)
    const hold = offset(cfg.holdRadiusFactor)
    out.x += bx * hold
    out.y += by * hold
    return
  }

  if (order === 'walk-on') {
    // Drive. Hold station on the near side at the driving distance and let the flock's own flight
    // carry it away — the dog follows because the target tracks the mob.
    //
    // Each repeat leans in by `walkOnPressStep`, floored well short of zero: a dog standing ON the
    // mob is not maximum pressure, it is a scattered flock and a lost run.
    //
    // **Pace is 1-based, and the base pace leans by nothing.** Giving the command puts the dog at
    // pace 1, which is P17's slow steady walk unchanged; only repeats bring it in. The alternative —
    // a base of 0 — meant the first walk-on had no notch on the dial, so "steady" had nothing to
    // wind back and the button it lit never changed. A level the player can see and cannot alter is
    // worse than no level at all.
    const press = w.pressSteps < cfg.walkOnPressMax ? w.pressSteps : cfg.walkOnPressMax
    const leanSteps = press > 1 ? press - 1 : 0
    const leaned = cfg.walkOnRadiusFactor - leanSteps * cfg.walkOnPressStep

    // --- which way to push them ---
    //
    // The station sits opposite the direction of travel, so choosing where to stand IS choosing
    // where the mob goes. Pushing along whatever line the dog happens to be on is how a drive ends
    // up herding stock into a corner: it has no opinion about the destination at all.
    let sx = ux
    let sy = uy

    // 1. Aim at the goal, by at most `walkOnLead`°.
    //
    //    Balance — the station opposite the gate — is where a drive would ideally push from, so the
    //    line swings toward it. The swing starts from `driveAnchor`, the radial the dog stood on
    //    when the order was given, NOT from where the dog is now: an anchor that follows the dog is
    //    an unbounded correction, and it converges on balance, which is "walk on" doing the outrun
    //    for the player (PLAN.md § P8).
    //
    //    No angle is ever formed. The cap is a stored cosine/sine, and the rotation is built from
    //    the component of balance perpendicular to the anchor — so this is `+ − × ÷` and one sqrt,
    //    as the determinism rules require.
    let ax = w.driveAnchorX
    let ay = w.driveAnchorY
    if (ax === 0 && ay === 0) {
      ax = ux
      ay = uy
    }
    let bx = w.flock.x - w.gate.x
    let by = w.flock.y - w.gate.y
    const bd = Math.sqrt(bx * bx + by * by)
    if (bd > DEGENERATE) {
      bx /= bd
      by /= bd
      const cos = ax * bx + ay * by
      // The part of balance at right angles to the anchor, whose length is the sine of the angle
      // between them.
      const px = bx - ax * cos
      const py = by - ay * cos
      const sin = Math.sqrt(px * px + py * py)
      if (cos >= cfg.walkOnLeadCos) {
        // Already within the cap: aim straight at balance.
        sx = bx
        sy = by
      } else if (sin > DEGENERATE) {
        // Rotate the anchor toward balance by exactly the cap.
        sx = ax * cfg.walkOnLeadCos + (px / sin) * cfg.walkOnLeadSin
        sy = ay * cfg.walkOnLeadCos + (py / sin) * cfg.walkOnLeadSin
      }
    }

    // 2. Work the fence line rather than into it.
    //
    //    A push drives the mob along −s, so it is heading at the fence whenever that has a component
    //    against `fenceN`, the normal pointing back toward the mob. That component — and ONLY that
    //    component — is removed, which leaves the along-the-fence part of the push untouched and
    //    turns a drive into the wire into a drive down it.
    //
    //    **Removing a component, not switching to the fence direction.** An override reads better in
    //    a sentence and behaves worse: it is a hard flip on a strict inequality, so a drive whose
    //    line sits near the fence's own toggles between two answers every tick. Measured on the
    //    daily gate, the override cost two `dogleg` seeds — a layout that is nothing but fences —
    //    and blew the worst case out to 268 s. This version is continuous: when the push is already
    //    parallel to the fence it changes nothing at all.
    if (w.fenceAx !== 0 || w.fenceAy !== 0) {
      const px = -sx
      const py = -sy
      const into = px * w.fenceNx + py * w.fenceNy
      // Heading at the gate? Then the fence in the way is the one the gate is a hole in, and posting
      // them through it is the entire point of the run.
      let gx = w.gate.x - w.flock.x
      let gy = w.gate.y - w.flock.y
      const gd = Math.sqrt(gx * gx + gy * gy)
      const atGate = gd > DEGENERATE && (px * gx + py * gy) / gd > cfg.walkOnFenceSkipCos
      if (into < 0 && !atGate) {
        const qx = px - w.fenceNx * into
        const qy = py - w.fenceNy * into
        const qd = Math.sqrt(qx * qx + qy * qy)
        if (qd > DEGENERATE) {
          sx = -qx / qd
          sy = -qy / qd
        } else {
          // Square into the fence: there is no along-the-fence part left to keep, and this is the
          // worst case rather than an edge case — it is a dog driving stock straight at the wire.
          // Fall back to the fence's own direction, which the simulation has already oriented so
          // that following it is progress toward the gate.
          sx = -w.fenceAx
          sy = -w.fenceAy
        }
      }
    }

    w.rearAlong(sx, sy, out)
    const k = offset(leaned > 0.12 ? leaned : 0.12)
    out.x += sx * k
    out.y += sy * k
    return
  }

  if (order === 'thatll-do') {
    // Come off the sheep: straight out along the radial to beyond the pressure radius.
    w.rearAlong(ux, uy, out)
    const off = offset(cfg.recallRadiusFactor)
    out.x += ux * off
    out.y += uy * off
    return
  }

  // --- come by / away to me ---
  //
  // Run around the mob at the working distance. The heading is a tangential component plus a
  // radial correction, so a dog that is too close spirals out to station while it circles rather
  // than orbiting at whatever radius it happened to start from.
  //
  // World +y is DOWN on screen (the renderer maps world y straight to canvas y with no flip), so
  // `(x,y) → (−y,x)` is the clockwise quarter-turn as the player sees it. Come by is the clockwise
  // flank, away to me its mirror.
  const sign = order === 'come-by' ? 1 : -1
  const tx = -uy * sign
  const ty = ux * sign

  const flankStation = station(cfg.flankRadiusFactor)
  // Positive when too far out, so the correction pulls inward along −u.
  const err = (d - flankStation) * cfg.flankRadialGain
  const pull = err > 1 ? 1 : err < -1 ? -1 : err

  /**
   * Getting OUT takes priority over getting round.
   *
   * The correction is deliberately asymmetric. Blending tangent and radial equally means a dog that
   * finds itself inside the working radius runs a 45° spiral — which is a path straight across the
   * mob, and measured exactly that: 4.0 m from the flock's centre, threading between animals while
   * dutifully keeping 3.5 m from each one. Per-sheep avoidance cannot express "go around them",
   * because a spread mob always leaves a corridor.
   *
   * So while the dog is inside its station the tangent is damped and the radial dominates: it backs
   * off first and arcs second, which is both what a dog does and what makes a flank read as a cast.
   */
  //
  // Graded rather than a switch. Damping the tangent the moment the dog is a hair inside its station
  // turns every ordinary flank into a detour, and it costs solvability: measured, a hard switch took
  // the per-template gate from 140/140 to 137/140 while the old even blend held it. The dog only
  // needs to bail out when it is genuinely AMONG them, so this ramps from full tangent at the
  // station to `flankEscapeTangent` well inside it.
  const escapeFrom = flankStation * 0.75
  const tangentWeight =
    d >= flankStation
      ? 1
      : d <= escapeFrom
        ? cfg.flankEscapeTangent
        : cfg.flankEscapeTangent +
          (1 - cfg.flankEscapeTangent) * ((d - escapeFrom) / (flankStation - escapeFrom))

  let hx = tx * tangentWeight - ux * pull
  let hy = ty * tangentWeight - uy * pull
  const hd = Math.sqrt(hx * hx + hy * hy)
  if (hd < DEGENERATE) {
    // Tangent and correction cancelled exactly. Run the tangent alone rather than stalling.
    hx = tx
    hy = ty
  } else {
    hx /= hd
    hy /= hd
  }

  out.x = w.dog.x + hx * w.lookahead
  out.y = w.dog.y + hy * w.lookahead
}
